import { BiCheck } from "react-icons/bi";
import { useDispatch } from "react-redux";
import {
  hideLoginModal,
  showRegisterModal,
} from "../../store/auth/login/actions";
import { BillingCycle, Plan, formatLimit, formatPrice } from "./plans";
import {
  WebPlan,
  savePendingPlan,
  webBillingEnabled,
} from "../../services/webBilling";

interface PricingCardProps {
  plan: Plan;
  billingCycle: BillingCycle;
  recommended?: boolean;
  userId: string;
  userEmail: string;
  isPaid: boolean;
  userTier: "free" | "lite" | "pro" | "elite" | "none" | null;
  buttonLabel?: string;
  // Web checkout for this plan and cycle, once loaded for a logged-in user.
  webPlan?: WebPlan;
  onCheckout?: () => void;
  checkoutBusy?: boolean;
  checkoutError?: string | null;
}

const LimitMeter = ({
  label,
  limit,
  percent,
  highlight,
}: {
  label: string;
  limit: number;
  percent: number;
  highlight: boolean;
}) => (
  <div>
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="font-semibold text-white">{formatLimit(limit)}</span>
    </div>
    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
      <div
        className={`h-full rounded-full ${
          highlight
            ? "bg-gradient-to-r from-blue-400 to-violet-400"
            : "bg-blue-500/80"
        }`}
        style={{ width: `${percent}%` }}
      />
    </div>
  </div>
);

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  billingCycle,
  recommended = false,
  userId,
  userEmail,
  isPaid,
  userTier,
  buttonLabel,
  webPlan,
  onCheckout,
  checkoutBusy = false,
  checkoutError = null,
}) => {
  const dispatch = useDispatch();

  const highlight = plan.key === "pro";
  const selectedPrice =
    billingCycle === "monthly" ? plan.monthlyPrice : plan.annualPrice;
  const isCurrentTier = plan.key === userTier;
  const isFreePlan = plan.key === "free";
  // The web product says whether it has a trial. Before it loads (or with
  // web checkout off) match the app, where only Pro monthly has one.
  const trialDays = webPlan
    ? webPlan.trialDays
    : plan.key === "pro" && billingCycle === "monthly"
      ? 30
      : null;
  const hasTrial = !!trialDays && !isPaid;

  const startFree = () => {
    dispatch(hideLoginModal());
    dispatch(showRegisterModal());
  };

  const handleSubscribe = async () => {
    if (isFreePlan && userId) {
      window.location.href = "/inventory";
      return;
    }

    // Most visitors here are new, so open sign-up rather than log-in.
    // With web checkout on, it opens for this plan once they're signed in.
    if (!userId || !userEmail) {
      if (webBillingEnabled && !isFreePlan) {
        savePendingPlan(plan.key as "lite" | "pro", billingCycle);
      }
      startFree();
      return;
    }

    if (!webBillingEnabled) {
      window.alert(
        hasTrial
          ? "Start your 30-day free trial of Pro in the VendorOrbis app on iPhone or Android. Log in with this account and choose Pro."
          : "Subscriptions are purchased in the VendorOrbis app on iPhone or Android. Log in with this account to subscribe or manage your plan."
      );
      return;
    }

    if (isPaid) {
      window.location.href = "/billing";
    } else {
      onCheckout?.();
    }
  };

  let ctaLabel: string;
  let ctaAction: () => void = handleSubscribe;
  let ctaDisabled = false;
  if (isFreePlan && isCurrentTier) {
    ctaLabel = buttonLabel || "Current Plan";
  } else if (isFreePlan) {
    ctaLabel = buttonLabel || (userId ? "Go to Dashboard" : "Start for free");
  } else if (checkoutBusy) {
    ctaLabel = "Opening checkout...";
    ctaDisabled = true;
  } else if (hasTrial) {
    ctaLabel = `Start ${trialDays}-day free trial`;
  } else if (!webBillingEnabled) {
    ctaLabel = isPaid ? "Manage in the app" : "Subscribe in the app";
  } else if (isPaid && !isCurrentTier && userTier !== "none") {
    ctaLabel = "View Billing";
    ctaAction = () => (window.location.href = "/billing");
  } else if (isPaid && isCurrentTier) {
    ctaLabel = "Manage Subscription";
  } else {
    ctaLabel = "Subscribe";
  }

  const priceSuffix = isFreePlan
    ? "forever"
    : billingCycle === "monthly"
      ? "/month"
      : "/year";
  const priceNote = isFreePlan
    ? "No credit card. No time limit."
    : hasTrial
      ? `${trialDays} days free, then ${formatPrice(selectedPrice)}/${
          billingCycle === "monthly" ? "month" : "year"
        }. Cancel anytime.`
      : billingCycle === "monthly"
        ? "Billed monthly"
        : `About ${formatPrice(
            Math.round((plan.annualPrice / 12) * 100) / 100
          )}/month, billed ${formatPrice(plan.annualPrice)} yearly`;

  const content = (
    <div
      className={`relative flex h-full flex-col rounded-[23px] p-7 md:p-8 ${
        highlight ? "bg-[#0d1528]" : ""
      }`}
    >
      <div className="flex min-h-[28px] items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-white">{plan.title}</h3>
        <div className="flex gap-2">
          {recommended && (
            <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-400/30">
              Fits you
            </span>
          )}
          {plan.badge && (
            <span className="rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-2.5 py-1 text-xs font-semibold text-white">
              {plan.badge}
            </span>
          )}
        </div>
      </div>
      <p className="mt-1 text-sm text-slate-400">{plan.tagline}</p>

      <div className="mt-6 flex items-end gap-1.5">
        <span className="text-5xl font-bold tracking-tight text-white">
          {formatPrice(selectedPrice)}
        </span>
        <span className="pb-1.5 text-sm text-slate-400">{priceSuffix}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">{priceNote}</p>

      {isPaid &&
        userTier !== "none" &&
        userTier !== "free" &&
        !isCurrentTier &&
        plan.key === "lite" && (
          <p className="mt-3 text-sm font-semibold text-emerald-400">
            You're already on a higher plan
          </p>
        )}

      <button
        type="button"
        onClick={ctaAction}
        disabled={ctaDisabled}
        className={`mt-6 w-full rounded-xl px-5 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
          highlight
            ? "bg-gradient-to-r from-blue-500 to-violet-500 shadow-lg shadow-blue-500/25 hover:brightness-110"
            : "border border-white/10 bg-white/10 hover:bg-white/15"
        }`}
      >
        {ctaLabel}
      </button>
      {hasTrial && (
        <p className="mt-2 text-center text-xs text-slate-500">
          {webBillingEnabled
            ? "Nothing to pay today."
            : "Nothing to pay today. Starts in the mobile app."}
        </p>
      )}
      {!isFreePlan && !userId && (
        <button
          type="button"
          onClick={startFree}
          className="mt-3 w-full text-center text-sm text-slate-400 transition hover:text-white"
        >
          Not ready to pay?{" "}
          <span className="font-semibold text-blue-400 underline-offset-2 hover:underline">
            Start for free
          </span>
        </button>
      )}
      {checkoutError && (
        <p className="mt-2 text-sm text-red-400">{checkoutError}</p>
      )}

      <div className="mt-7 space-y-4">
        <LimitMeter
          label="Active events"
          limit={plan.limits.events}
          percent={plan.meter}
          highlight={highlight}
        />
        <LimitMeter
          label="Inventory items"
          limit={plan.limits.items}
          percent={plan.meter}
          highlight={highlight}
        />
      </div>

      <div className="my-7 h-px bg-white/10" />

      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
        {plan.featuresHeading}
      </p>
      <ul className="mt-4 space-y-3">
        {plan.features.map((feature) => (
          <li key={feature} className="flex gap-3 text-sm text-slate-300">
            <span
              className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
                highlight
                  ? "bg-gradient-to-br from-blue-500 to-violet-500 text-white"
                  : "bg-blue-500/15 text-blue-400"
              }`}
            >
              <BiCheck size={14} />
            </span>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );

  const recommendedRing = recommended
    ? "ring-2 ring-emerald-400/60 ring-offset-4 ring-offset-[#070b17]"
    : "";

  if (highlight) {
    // A conic gradient spins behind a 1px gap to draw the animated border.
    return (
      <div
        id={`plan-${plan.key}`}
        className={`relative h-full overflow-hidden rounded-3xl p-px shadow-[0_0_90px_-25px_rgba(99,102,241,0.75)] transition duration-300 ${recommendedRing}`}
      >
        <div
          aria-hidden
          className="absolute -inset-full animate-[spin_7s_linear_infinite] bg-[image:conic-gradient(from_0deg,transparent_0deg,#60a5fa_70deg,#a78bfa_140deg,transparent_220deg)] motion-reduce:animate-none"
        />
        {content}
      </div>
    );
  }

  return (
    <div
      id={`plan-${plan.key}`}
      className={`h-full rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-white/20 ${recommendedRing}`}
    >
      {content}
    </div>
  );
};

export default PricingCard;
